import { useState, useEffect, useMemo } from 'react'
import { Typography, Box, Stack, Tabs, Tab, LinearProgress, Chip, Select, MenuItem, FormControl, Button } from '@mui/material'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import SummaryTab from './SummaryTab'
import LectureListTab from './LectureListTab'
import NoticeTab from './NoticeTab'
import SurveyTab from './SurveyTab'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import { fetchMyManagementComboList } from '@/api/mypage'
import { useAuth } from '@/components/AuthContext'

export function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

export default function MyManagementView() {
  const { REACT_APP_ADMIN_PAGE_PATH } = process.env;
  const navigate = useNavigate()
  const lang = useLang() 
  const [searchParams, setSearchParams] = useSearchParams()
  const { id } = useParams()
  const { isMobile } = useIsMobile()
  const { user } = useAuth();

  const [data, setData] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // console.log("######fetchMyManagementComboList : ");
        const response = await fetchMyManagementComboList({ params: { lecturerAccountId: user.accountId } });
        const courses = response.data || [];
        setCourseList(courses);

        const currentCourse = courses.find(course => String(course.code) === String(id));
        setData(currentCourse || null);

      } catch (error) {
        console.error("Failed to fetch management list:", error);
        setData(null);
        setCourseList([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    } else {
        setLoading(false);
        setData(null);
        setCourseList([]);
    }
  }, [id]);

  // 탭 관련
  const tabKeys = ['list', 'intro', 'notice', 'survey']
  const initialTab =
    tabKeys.indexOf(searchParams.get('tab')) >= 0
      ? tabKeys.indexOf(searchParams.get('tab'))
      : 0
  const [value, setValue] = useState(initialTab)

  const handleChange = (event, newValue) => {
    const newTab = tabKeys[newValue]
    const current = Object.fromEntries(searchParams.entries())
    const nextParams = { ...current, tab: newTab }
    delete nextParams.id
    const queryString = new URLSearchParams(nextParams).toString()
    const targetUrl = `${window.location.pathname}?${queryString}`

    window.history.pushState({}, '', targetUrl)
    setSearchParams(nextParams)
    setValue(newValue)
  }

  const handleLectureChange = (e) => {
    const nextId = e.target.value
    navigate(`/mypage/management/${nextId}?tab=${searchParams.get('tab') ?? 'list'}`)
  }

  useEffect(() => {
    const urlTab = searchParams.get('tab')
    const idx = tabKeys.indexOf(urlTab)
    if (idx >= 0 && idx !== value) setValue(idx)
  }, [searchParams])

  if (loading) {
    return <LinearProgress />;
  }

  if (!data) {
    return <Typography sx={{ py: 5, textAlign: 'center' }}>강좌 정보를 찾을 수 없습니다.</Typography>
  }

  const statusColor = {
    강의중: 'primary',
    대기중: 'secondary',
    종강: 'secondary2',
  }[data.codeEtc] || 'grey'

  return (
    <Box>
      {/* 상단 강좌 정보 */}
      <Box
        sx={{
          width: '100%',
          bgcolor: 'grey.50',
          borderRadius: 2,
          py: 2,
          px: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: isMobile? 5 : 6,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Chip label={data.codeEtc} variant="outlined" color={statusColor} />
          <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '320px' }}>
            <Select
              className="transparent"
              name="lecture"
              value={data.code}
              onChange={handleLectureChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: (theme) => 
                      isMobile ? `calc(100vw - ${theme.spacing(20)})` : '100%',
                    maxWidth: (theme) =>
                      isMobile ? `calc(100vw - ${theme.spacing(20)})` : '320px',
                  },
                },
              }}
              sx={{
                width: (theme) => 
                  isMobile ? `calc(100vw - ${theme.spacing(20)})` : '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(20)})` : '320px',
                  typography: 'subtitle2',
                  fontWeight: '600'
              }}
            >
              {courseList.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.codeNm}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* 탭 */}
      <Tabs
        value={value}
        onChange={handleChange}
        onClick={(e) => {
          const tabEls = e.currentTarget.querySelectorAll('.MuiTab-root')
          const clickedTab = e.target.closest('.MuiTab-root')

          if (!clickedTab) return 

          const clickedIndex = Array.from(tabEls).indexOf(clickedTab)

          if (clickedIndex === value) {
            handleChange(e, value)
          }
        }}
        variant="fullWidth"
        aria-label="강좌관리 상세 탭"
        textColor="inherit"
        TabIndicatorProps={{
          sx: { height: '2px', bgcolor: 'grey.800' },
        }}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': {
            color: 'grey.500',
            px: isMobile ? 2 : 4,
            flex: '1 1 0',
            typography: 'body1',
            fontWeight: '600',
            minWidth: 'initial',
            height: '48px',
            lineHeight: 1,
            textTransform: 'none',
            whiteSpace: 'nowrap'
          },
          '& .Mui-selected': {
            color: 'grey.800',
          },
        }}
      >
        <Tab label="강좌요약" />
        <Tab label="강좌개요" />
        <Tab label="공지사항" />
        {/* <Tab label="설문" /> */}
      </Tabs>

      {/* 탭 콘텐츠 */}
      <TabPanel value={value} index={0}>
        <SummaryTab />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <LectureListTab />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <NoticeTab />
      </TabPanel>
      {/* <TabPanel value={value} index={3}>
        <SurveyTab />
      </TabPanel> */}
      
      {/* 하단 버튼 */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: isMobile ? 1 : 3, gap: 1 }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="large" 
          sx={{ width: isMobile ? '100%' : 'auto' }}
          onClick={() => navigate(buildLangHref('/mypage/management', lang))}
        >
          강좌목록
        </Button>
        {value === 2 && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ width: isMobile ? '100%' : 'auto' }}
            component="a"
            href={REACT_APP_ADMIN_PAGE_PATH}
            target="_blank"
            rel="noopener noreferrer"
          >
            관리
          </Button>
        )}
      </Stack>
    </Box>
  )
}
